import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { Eyebrow } from "./Reveal";
import { scrollToSection } from "./Nav";

const LINES = ["WEBSITES THAT", "MAKE BUSINESSES", "LOOK BETTER."];

export default function Hero() {
  const { scrollY } = useScroll();
  const yDrift = useTransform(scrollY, [0, 700], [0, 110]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.25]);

  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden">
      <motion.div
        style={{ y: yDrift, opacity: fade }}
        className="max-w-[1400px] mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-20 md:pb-28"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="flex items-center gap-4"
        >
          <span className="h-px w-10 bg-accent" aria-hidden="true" />
          <Eyebrow>Freelance Web Developer</Eyebrow>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="mt-8 font-heading font-medium uppercase tracking-tight leading-[0.92] text-[13.5vw] sm:text-[11vw] lg:text-[8.2vw] text-ink"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-[0.06em] -mb-[0.06em]">
              <motion.span
                className="block will-change-transform"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.05, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 2 ? (
                  <>
                    LOOK <em className="text-accent not-italic font-semibold italic">BETTER.</em>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 md:mt-14 grid md:grid-cols-12 gap-8 items-end">
          <motion.p
            data-testid="hero-subcopy"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="md:col-span-5 text-base md:text-lg leading-relaxed text-stone max-w-md"
          >
            I design and build modern, responsive websites for businesses that
            want a stronger presence online.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="md:col-span-7 flex flex-wrap items-center gap-4 md:justify-end"
          >
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); scrollToSection("#contact"); }}
              data-testid="hero-cta-start-project"
              className="group inline-flex items-center gap-2 bg-ink text-ivory font-semibold tracking-wide px-7 py-4 hover:bg-accent transition-colors duration-300"
            >
              Start a Project
              <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#work"
              onClick={(e) => { e.preventDefault(); scrollToSection("#work"); }}
              data-testid="hero-cta-view-work"
              className="group inline-flex items-center gap-2 border border-ink/30 text-ink font-semibold tracking-wide px-7 py-4 hover:border-ink hover:bg-ink hover:text-ivory transition-colors duration-300"
            >
              View Selected Work
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
          </motion.div>
        </div>

        <motion.p
          data-testid="hero-supporting-line"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.25 }}
          className="mt-12 text-xs sm:text-sm tracking-[0.18em] uppercase text-stone"
        >
          Custom design <span className="text-accent mx-1">•</span> Responsive development <span className="text-accent mx-1">•</span> AI-assisted workflow
        </motion.p>
      </motion.div>
    </section>
  );
}
